import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { parentClaimOpen } from "./domain/auth";
import { localDevShortcutsEnabled, runtimeFromBrowser } from "./domain/env";
import { useAuth } from "./state/AuthProvider";
import { EnvironmentBanner } from "./components/EnvironmentBanner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AppLoader } from "./components/LogoSpin";
import { LayoutProvider } from "./state/LayoutProvider";
import { FamilyHome } from "./pages/FamilyHome";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ParentClaim } from "./pages/ParentClaim";
import { Gate } from "./pages/Gate";
import { PublicEvent } from "./pages/PublicEvent";
import { PublicEvents } from "./pages/PublicEvents";
import { Register } from "./pages/Register";
import { RegisterDone } from "./pages/RegisterDone";
import { ResetPassword } from "./pages/ResetPassword";
import { SignIn } from "./pages/SignIn";
import { SignUp } from "./pages/SignUp";
import { StaffCheckIn } from "./pages/StaffCheckIn";
import { StaffCreateEvent } from "./pages/StaffCreateEvent";
import { StaffCreateForm } from "./pages/StaffCreateForm";
import { StaffEditForm } from "./pages/StaffEditForm";
import { StaffEvent } from "./pages/StaffEvent";
import { StaffEventForm } from "./pages/StaffEventForm";
import { StaffEvents } from "./pages/StaffEvents";
import { StaffForm } from "./pages/StaffForm";
import { StaffForms } from "./pages/StaffForms";
import { StaffInbox } from "./pages/StaffInbox";
import { StaffRoster } from "./pages/StaffRoster";
import { StaffSettings } from "./pages/StaffSettings";
import { StaffUploadStudents } from "./pages/StaffUploadStudents";
import { StudentEvent } from "./pages/StudentEvent";
import { StudentEvents } from "./pages/StudentEvents";
import { StudentHome } from "./pages/StudentHome";
import { StaffCreateStudent, StudentEdit } from "./pages/StudentEdit";
import { StudentProfile } from "./pages/StudentProfile";
import { StudentSignIn } from "./pages/StudentSignIn";

const TestHarnessPage = import.meta.env.DEV
  ? lazy(() =>
      import("./pages/TestHarness").then((mod) => ({
        default: mod.TestHarnessPage,
      })),
    )
  : null;

function MigrationClaimGate() {
  const location = useLocation();
  const { account, loading } = useAuth();
  if (loading || !account || !parentClaimOpen(account)) return null;
  if (location.pathname === "/claim") return null;
  return <Navigate to="/claim" replace />;
}

function DemoAccountSync() {
  const location = useLocation();
  const { configureDemoAccounts } = useAuth();
  const enabled = localDevShortcutsEnabled(
    runtimeFromBrowser().name,
    location.search,
  );
  useEffect(() => {
    configureDemoAccounts(enabled);
  }, [configureDemoAccounts, enabled]);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppLoader />
        <DemoAccountSync />
        <MigrationClaimGate />
        <LayoutProvider>
          <EnvironmentBanner />
          <Routes>
            <Route path="/" element={<Gate />} />
            <Route path="/events" element={<PublicEvents />} />
            <Route path="/events/:eventId" element={<PublicEvent />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signin/student" element={<StudentSignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/claim" element={<ParentClaim />} />
            <Route path="/student/:studentId" element={<StudentHome />} />
            <Route path="/student/:studentId/profile" element={<StudentProfile />} />
            <Route path="/student/:studentId/edit" element={<StudentEdit />} />
            <Route path="/student/:studentId/events" element={<StudentEvents />} />
            <Route
              path="/student/:studentId/events/:eventId"
              element={<StudentEvent />}
            />

            {/* Parent must be signed in. The form always saves onto their household. */}
            <Route
              element={
                <ProtectedRoute allowedRoles={["parent"]} redirectTo="/signin" />
              }
            >
              <Route path="/register" element={<Register />} />
              <Route path="/register/done/:studentId" element={<RegisterDone />} />
            </Route>

            <Route
              element={
                <ProtectedRoute
                  allowedRoles={["parent", "admin", "staff"]}
                  redirectTo="/signin"
                />
              }
            >
              <Route path="/family" element={<FamilyHome />} />
            </Route>

            {/* Protected Staff routes */}
            <Route
              element={
                <ProtectedRoute
                  allowedRoles={["staff", "admin"]}
                  redirectTo="/signin?role=staff"
                />
              }
            >
              <Route path="/staff" element={<StaffRoster />} />
              <Route path="/staff/inbox" element={<StaffInbox />} />
              <Route path="/staff/settings" element={<StaffSettings />} />
              <Route path="/staff/students/new" element={<StaffCreateStudent />} />
              <Route path="/staff/students/upload" element={<StaffUploadStudents />} />
              <Route path="/staff/forms" element={<StaffForms />} />
              <Route path="/staff/forms/new" element={<StaffCreateForm />} />
              <Route path="/staff/forms/:formId/edit" element={<StaffEditForm />} />
              <Route path="/staff/forms/:formId" element={<StaffForm />} />
              <Route path="/staff/events" element={<StaffEvents />} />
              <Route path="/staff/events/new" element={<StaffCreateEvent />} />
              <Route
                path="/staff/events/:eventId/form"
                element={<StaffEventForm />}
              />
              <Route
                path="/staff/events/:eventId/check-in"
                element={<StaffCheckIn />}
              />
              <Route path="/staff/events/:eventId" element={<StaffEvent />} />
            </Route>

            {TestHarnessPage ? (
              <Route
                path="/harness"
                element={
                  <Suspense
                    fallback={
                      <div className="app">
                        <main className="shell">
                          <p className="meta">Loading test harness</p>
                        </main>
                      </div>
                    }
                  >
                    <TestHarnessPage />
                  </Suspense>
                }
              />
            ) : null}

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </LayoutProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
